import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { useAdminProperties } from '@/hooks/admin/useAdminProperties';
import { useGetIssuesForProperty } from '@/hooks/issues/useGetIssuesForProperty';
import { useProperties } from '@/hooks/properties';
import { useRecentPropertyIds } from '@/hooks/useRecentPropertyIds';
import { useUserRole } from '@/hooks/useUserRole';
import { Issue } from '@/models/Issue';
import { UserProperty } from '@/models/Property';
import { logPropertyView } from '@/services/api/activity';
import { adminPropertyToUserProperty } from '@/services/api/admin';

type PropertyIssuesByCategory = {
  name: string;
  issues: Issue[];
};

interface PropertiesContextProps {
  properties?: UserProperty[];
  isInitialFetchingProperties: boolean;
  isFetchingProperties: boolean;
  selectedProperty?: UserProperty;
  setSelectedProperty: (property: UserProperty) => void;
  selectFavoriteProperty: () => void;
  propertyIssues?: Issue[];
  propertyIssuesByCategory: PropertyIssuesByCategory[];
  isInitialFetchingPropertyIssues: boolean;
  isFetchingPropertyIssues: boolean;
  hasOwnedProperties: boolean;
  canEditSelectedProperty: boolean;
}

export const PropertiesContext = createContext<PropertiesContextProps | null>(null);

export const usePropertiesContext = () => {
  const context = useContext(PropertiesContext);

  if (!context)
    throw new Error('usePropertiesContext context must be used inside PropertiesProvider');

  return context;
};

const getPersistedPropertyKey = (userId: string) => `blueprint_selected_property_id_${userId}`;

const readPersistedId = (userId: string): string | null => {
  try {
    return localStorage.getItem(getPersistedPropertyKey(userId));
  } catch {
    return null;
  }
};

// Advisors/admins use sessionStorage so refresh keeps the selection within
// a tab, but a new login (new tab) starts agnostic.
const getAdvisorSessionKey = (userId: string) => `blueprint_advisor_selected_property_id_${userId}`;

const readAdvisorSessionId = (userId: string): string | null => {
  try {
    return sessionStorage.getItem(getAdvisorSessionKey(userId));
  } catch {
    return null;
  }
};

export const PropertiesProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthContext();
  const { canViewAllProperties, isAdmin, isAdvisor } = useUserRole();
  const [selectedProperty, setSelectedPropertyRaw] = useState<UserProperty>();
  const { pushRecentId } = useRecentPropertyIds();

  const setSelectedProperty = useCallback(
    (property: UserProperty) => {
      setSelectedPropertyRaw(property);
      pushRecentId(property.property.id);
      if (user?.id) {
        try {
          if (canViewAllProperties) {
            sessionStorage.setItem(getAdvisorSessionKey(user.id), property.property.id);
          } else {
            localStorage.setItem(getPersistedPropertyKey(user.id), property.property.id);
          }
        } catch {
          // ignore storage errors (e.g. private browsing quota)
        }
      }
    },
    [pushRecentId, user?.id, canViewAllProperties],
  );

  // Clear selected property when user logs out or changes
  useEffect(() => {
    if (!user) {
      setSelectedPropertyRaw(undefined);
    }
  }, [user]);

  const {
    isInitialFetching: isInitialFetchingProperties,
    isFetching: isFetchingProperties,
    properties,
  } = useProperties();

  // For advisors/admins: fetch all properties to enable restoring persisted selection on refresh.
  // For buyers this query is disabled (canViewAllProperties is false).
  const { properties: _adminProperties } = useAdminProperties({ enabled: canViewAllProperties });

  const {
    isInitialFetching: isInitialFetchingPropertyIssues,
    isFetching: isFetchingPropertyIssues,
    issues: propertyIssues,
  } = useGetIssuesForProperty(selectedProperty?.property.id || '');

  const propertyIssuesByCategory = useMemo(
    () =>
      (propertyIssues ?? []).reduce<PropertyIssuesByCategory[]>((categories, issue) => {
        const category = categories.find((category) => category.name === issue.category);
        if (category) {
          category.issues.push(issue);
        } else {
          categories.push({ name: issue.category, issues: [issue] });
        }
        return categories;
      }, []),
    [propertyIssues],
  );

  // Buyers: restore persisted selection (or fall back to favorite/first) when properties load.
  // Also clears selection if a buyer removes all their properties.
  useEffect(() => {
    if (canViewAllProperties || !properties) return;

    if (properties.length === 0) {
      if (selectedProperty) setSelectedPropertyRaw(undefined);
      return;
    }

    // If the selected property is still in the list, nothing to do.
    // If it's gone (e.g. advisor marked it Passed and backend removed access),
    // fall through and pick a new property.
    if (selectedProperty && properties.some((p) => p.property.id === selectedProperty.property.id))
      return;

    const persistedId = user?.id ? readPersistedId(user.id) : null;
    const persistedProperty = persistedId
      ? properties.find((p) => p.property.id === persistedId)
      : undefined;
    const favoriteProperty = properties.find((p) => p.isFavorite);
    const ownedProperty = properties.find((p) => p.isOwned);
    setSelectedProperty(persistedProperty || ownedProperty || favoriteProperty || properties[0]);
  }, [properties, selectedProperty, canViewAllProperties, user?.id, setSelectedProperty]);

  // Advisors/admins: agnostic on first login (sessionStorage is empty), but
  // restore from sessionStorage on refresh within the same tab so users don't
  // lose their working context.
  useEffect(() => {
    if (!canViewAllProperties || selectedProperty || !user?.id) return;
    if (!_adminProperties || _adminProperties.length === 0) return;
    const persistedId = readAdvisorSessionId(user.id);
    if (!persistedId) return;
    const match = _adminProperties.find((p) => p.id === persistedId);
    if (match) setSelectedPropertyRaw(adminPropertyToUserProperty(match));
  }, [canViewAllProperties, selectedProperty, user?.id, _adminProperties]);

  // Log property view when selected property changes
  const previousPropertyIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const propertyId = selectedProperty?.property.id;
    if (propertyId && propertyId !== previousPropertyIdRef.current) {
      previousPropertyIdRef.current = propertyId;
      // Fire and forget - don't block on the response
      logPropertyView(propertyId).catch((error) => {
        console.warn('Failed to log property view:', error);
      });
    }
  }, [selectedProperty?.property.id]);

  const selectFavoriteProperty = useCallback(() => {
    const favoriteProperty = properties?.find((property) => property.isFavorite);
    if (favoriteProperty) {
      setSelectedProperty(favoriteProperty);
    }
  }, [properties, setSelectedProperty]);

  // Screen-grab override — see ClientSidebar.tsx for the URL param +
  // localStorage capture. Applied here so the override also flips the
  // router redirect (DashboardRedirect → home-hub vs summary) and any
  // other consumer of `hasOwnedProperties`, not just the sidebar order.
  const readOwnerPreviewOverride = (): boolean | null => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem('previewOwnerSidebar');
      if (stored === '1') return true;
      if (stored === '0') return false;
    } catch {
      // ignore
    }
    return null;
  };
  const ownerPreviewOverride = readOwnerPreviewOverride();
  const computedHasOwnedProperties = (properties ?? []).some((p) => p.isOwned);
  const hasOwnedProperties =
    ownerPreviewOverride !== null ? ownerPreviewOverride : computedHasOwnedProperties;
  const canEditSelectedProperty = isAdmin || isAdvisor || (selectedProperty?.isOwned ?? false);

  const memoizedValue = useMemo(
    () => ({
      properties,
      isInitialFetchingProperties,
      isFetchingProperties,
      selectedProperty,
      setSelectedProperty,
      selectFavoriteProperty,
      propertyIssues,
      propertyIssuesByCategory,
      isInitialFetchingPropertyIssues,
      isFetchingPropertyIssues,
      hasOwnedProperties,
      canEditSelectedProperty,
    }),
    [
      properties,
      isInitialFetchingProperties,
      isFetchingProperties,
      selectedProperty,
      setSelectedProperty,
      selectFavoriteProperty,
      propertyIssues,
      propertyIssuesByCategory,
      isInitialFetchingPropertyIssues,
      isFetchingPropertyIssues,
      hasOwnedProperties,
      canEditSelectedProperty,
    ],
  );

  return <PropertiesContext.Provider value={memoizedValue}>{children}</PropertiesContext.Provider>;
};
